import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check, Sparkles, ArrowRight, ChevronDown, ShieldCheck,
  Calendar, Star, FileText, Leaf, Phone, X,
} from "lucide-react";
import Layout from "../components/Layout";
import PageBanner from "../components/PageBanner";
import { trackViewPlans, trackPlanSelected } from "../lib/ga";
import {
  PLAN_BASE, PROFILE_LABELS, SERVICE_USAGE_KWH,
  computePricings, zoneAverageRate as _zoneAverageRate,
} from "../lib/pricing";

// Map plan icon names from the shared module to actual lucide components.
const PLAN_ICONS = { ShieldCheck, Leaf, Star };

/**
 * PlanPage — Rhythm-style plan selector.
 *
 * Each card shows only the essentials at first glance:
 *   - Plan name + term
 *   - Monthly $ estimate (only when address verified)
 *   - Fixed rate ¢/kWh + zone average ¢/kWh
 *   - "Tarifa fija garantizada" badge + autopay savings badge
 *
 * Below "Elegir este plan" the customer can expand an inline panel that
 * reveals everything else: full feature list, EFL link, cancellation terms,
 * what's included with AutoPay. No more dense walls of text in the card.
 */

const SERVICE_BASES = {
  apartments:  { titleKey: "apartmentsTitle",  accentKey: "apartmentsAccent" },
  residential: { titleKey: "residentialTitle", accentKey: "residentialAccent" },
  commercial:  { titleKey: "commercialTitle",  accentKey: "commercialAccent" },
};

// =====================================================================
// Plan catalog + pricing now live in `/lib/pricing.js` (shared with the
// admin's ServicePlacement.jsx). This file only re-decorates the plans
// with their lucide icons for rendering.
// =====================================================================
const PLAN_TYPES = PLAN_BASE.map((p) => ({
  ...p,
  icon: PLAN_ICONS[p.iconName] || ShieldCheck,
}));

const SERVICE_TYPE_LABEL = {
  apartments:  "apartamento",
  residential: "casa",
  commercial:  "negocio",
};

export default function PlanPage({ serviceType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const zip = params.get("zip") || localStorage.getItem("velcor_zip") || "";
  const address = params.get("address") || localStorage.getItem("velcor_service_address") || "";
  const unit = params.get("unit") || localStorage.getItem("velcor_unit") || "";
  const city = params.get("city") || localStorage.getItem("velcor_city") || "";
  const esiidParam = params.get("esiid") || localStorage.getItem("velcor_esiid") || "";
  const utilityParam = params.get("utility") || localStorage.getItem("velcor_utility") || "";

  const cfg = SERVICE_BASES[serviceType] || SERVICE_BASES.residential;
  const hasVerifiedAddress = !!esiidParam;

  // The plan catalog is now type-based (saver12 / eco12 / secure24) and
  // shared across all service types. Pricing varies by ZIP + serviceType.
  const PLANS = PLAN_TYPES;

  // Detected profile (from RentCast on /address). It now drives the
  // "Tu propiedad detectada" banner copy (NOT the recommended card —
  // saver12 is the recommended one by default for everyone).
  const detectedProfileId = params.get("profile") || "";
  const detectedLabel = PROFILE_LABELS[detectedProfileId] || "";
  // Track which plan card has its details panel open (only one at a time).
  const [openDetailsId, setOpenDetailsId] = useState(null);

  // ── Guard (Feb 2026) ───────────────────────────────────────────────────
  // Strict: if the visitor lands on /apartments | /residential | /commercial
  // WITHOUT an ESIID lookup completed (no `velcor_esiid` in localStorage and
  // no `esiid` URL param), we redirect them to /address first. This stops
  // us from showing fabricated prices computed from a default 2,000 kWh
  // profile + a Texas-average rate — those numbers were misleading the
  // visitor before any utility/ZIP was captured. Once they finish /address,
  // they come back to the SAME plans page via the `?next=` query string
  // and see prices computed against their REAL utility.
  // Hooks first — guard placed AFTER all useState/useEffect calls so React's
  // rules-of-hooks are respected.
  useEffect(() => {
    if (hasVerifiedAddress) return;
    const next = `/${serviceType}`;
    const sp = new URLSearchParams();
    sp.set("next", next);
    sp.set("reason", "plans");
    navigate(`/address?${sp.toString()}`, { replace: true });
  }, [hasVerifiedAddress, navigate, serviceType]);

  // Funnel step 2/5 — fire ONCE when the visitor reaches a plan page with
  // a verified address. Source can be inferred from referrer / utm; we
  // pass `referrer_path` as a soft hint. Skipped when the guard redirects.
  useEffect(() => {
    if (!hasVerifiedAddress) return;
    let source = "direct";
    try {
      const ref = document.referrer || "";
      if (ref.includes("/electricidad-") || ref.includes("/electricity-")) source = "city_landing";
      else if (ref.endsWith("/") || ref.match(/^https?:\/\/[^/]+\/?$/)) source = "home_hero";
      else if (ref.includes("/address")) source = "address";
    } catch { /* noop */ }
    trackViewPlans({ serviceType, zip, source });
    // Run once per mount per serviceType — re-firing on params change
    // would inflate the count if React re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVerifiedAddress, serviceType]);

  const handleContinue = (planId) => {
    localStorage.setItem("velcor_selected_plan", planId);
    localStorage.setItem("velcor_service_type", serviceType);
    // Funnel step 3/5 — capture the specific plan + economics. Lookup the
    // plan config from the catalog so we send the displayed rate/term.
    try {
      const planCfg = PLANS.find((p) => p.id === planId) || {};
      // term is stored as "12 meses" / "24 meses" — strip non-digits so GA
      // gets a clean integer it can aggregate / filter on.
      const termInt = parseInt(String(planCfg.term || "").match(/\d+/)?.[0] || "0", 10);
      // rate is stored as e.g. "10.6" (text) — convert to cents int.
      const rateInt = Math.round(parseFloat(String(planCfg.rate || "0").replace(/[^\d.]/g, "")) * 10);
      trackPlanSelected({
        planId,
        planName: planCfg.name || planId,
        termMonths: termInt,
        rateCents: rateInt,
        avgBill: planCfg.avgBill || 0,
      });
    } catch { /* noop */ }
    const q = new URLSearchParams();
    if (zip) q.set("zip", zip);
    if (address) q.set("address", address);
    if (unit) q.set("unit", unit);
    if (city) q.set("city", city);
    if (esiidParam) q.set("esiid", esiidParam);
    if (utilityParam) q.set("utility", utilityParam);
    q.set("type", serviceType);
    q.set("plan", planId);
    navigate(`/enroll?${q.toString()}`);
  };

  const serviceLabel = t(`plans.${cfg.titleKey}`) + " " + t(`plans.${cfg.accentKey}`);

  // Don't flash the page content while the redirect is in flight.
  if (!hasVerifiedAddress) return null;

  return (
    <Layout hero hideFooter>
      <Helmet>
        <title>{serviceLabel} — Velcor Energy</title>
      </Helmet>

      <PageBanner
        eyebrow="Tu servicio confirmado"
        title="Elige el plan que mejor se ajusta a tu hogar"
        subtitle="Tarifa fija. Sin letra chica. Sin sorpresas en tu factura."
        as="h1"
        testId="plan-banner"
      />

      <div
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background:
            "radial-gradient(1100px 600px at 20% 0%, rgba(47,122,58,0.08) 0%, rgba(47,122,58,0) 60%), " +
            "radial-gradient(900px 500px at 90% 20%, rgba(47,122,58,0.05) 0%, rgba(47,122,58,0) 65%), " +
            "linear-gradient(180deg, #f8faf6 0%, #ffffff 60%)",
        }}
      />

      {/* Breadcrumb only — the eyebrow + h1 now live in the banner above */}
      <section className="max-w-[1180px] mx-auto px-5 pt-6 md:pt-8 pb-2">
        <div className="flex items-center gap-2 text-[12.5px] text-slate-500 font-bold">
          <a href="/" className="hover:text-velcor-green transition">← {t("nav.home")}</a>
          <span>›</span>
          <span className="text-velcor-ink">Elige tu plan</span>
        </div>
      </section>

      {/* Plan cards — 3 size tiers per service type */}
      <section className="max-w-[1180px] mx-auto px-5 pb-12 md:pb-20" data-testid="plans-grid">
        {/* Auto-detection callout — only when RentCast found something */}
        {detectedLabel && (
          <div
            className="mx-auto max-w-[640px] mb-5 rounded-2xl bg-velcor-green/8 border border-velcor-green/25 px-4 py-3 flex items-center gap-3"
            data-testid="auto-detected-banner"
          >
            <span className="grid place-items-center w-8 h-8 rounded-full bg-velcor-green text-white shrink-0">
              <Check className="w-4 h-4" strokeWidth={2.6} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-velcor-ink font-extrabold text-[13px] leading-tight">
                Detectamos: <span className="text-velcor-green">{detectedLabel}</span>
              </div>
              <div className="text-slate-500 text-[11.5px] mt-0.5">
                Precios calculados a {SERVICE_USAGE_KWH[serviceType]?.toLocaleString("en-US")} kWh/mes para tu zona ({zip || "TX"}).
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-7 mt-4 md:mt-6">
          {/* Shared pricing module computes all 3 plans + applies the $15
              Secure 24 floor. Same numbers the admin Step 3 shows. */}
          {computePricings({ zip, tdsp: utilityParam, serviceType }).map(({ plan: base, planRate, monthly, usage }, i) => {
            // Re-attach icon (lib has only iconName; here we map to lucide).
            const planWithIcon = PLAN_TYPES.find((p) => p.id === base.id) || { ...base, icon: ShieldCheck };
            const pricing = {
              planRate: planRate.toFixed(1),
              monthly,
              usage,
              zoneRate: _zoneAverageRate({ zip, tdsp: utilityParam, serviceType }).toFixed(1),
            };
            const isRecommended = !!base.accent;
            return (
              <PlanCard
                key={base.id}
                plan={planWithIcon}
                pricing={pricing}
                serviceType={serviceType}
                delay={i * 0.08}
                isRecommended={isRecommended}
                isDetected={false}
                hasVerifiedAddress={hasVerifiedAddress}
                detailsOpen={openDetailsId === base.id}
                onToggleDetails={() => setOpenDetailsId(openDetailsId === base.id ? null : base.id)}
                onChoose={() => handleContinue(base.id)}
              />
            );
          })}
        </div>

        {/* Trust strip */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-[800px] mx-auto text-center">
          <Trust icon={ShieldCheck} label="Tarifa fija garantizada" />
          <Trust icon={Star} label="Soporte 5 estrellas" />
          <Trust icon={Calendar} label="Cambio en 7 días" />
          <Trust icon={Sparkles} label="Compatible con todas las TDSP" />
        </div>
      </section>
    </Layout>
  );
}

/* ============================================================
   PlanCard — Rhythm/Energy-Texas inspired layout per latest user
   mockup. Top colored banner with marketing tagline, then plan
   name, short description, a horizontal divider, big rate display
   with info icon, "Enroll now" filled pill + "Learn More" outlined
   pill. Brand color = Velcor green (replaces Rhythm's purple).
   ============================================================ */
function PlanCard({
  plan, pricing, serviceType, delay = 0, isRecommended = false,
  isDetected = false, hasVerifiedAddress = false, detailsOpen = false,
  onToggleDetails, onChoose,
}) {
  // Eyebrow communicates plan personality ("MÁS POPULAR", "100% RENOVABLE")
  const banner = plan.eyebrow || "";

  return (
    <motion.div
      initial={{ opacity: 0, y: 18, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`relative rounded-3xl bg-white overflow-hidden flex flex-col border border-slate-200 ${
        isRecommended
          ? "ring-2 ring-velcor-green shadow-[0_18px_44px_-22px_rgba(47,122,58,0.32)]"
          : "shadow-[0_10px_30px_-22px_rgba(15,29,61,0.18)] hover:shadow-[0_22px_48px_-26px_rgba(15,29,61,0.22)]"
      } transition-shadow`}
      data-testid={`plan-card-${plan.id}`}
    >
      {/* Top tinted banner — Velcor green tint */}
      <div className="bg-velcor-green/15 px-5 py-2.5">
        <span
          className="text-velcor-green font-extrabold text-[11px] tracking-tight"
          data-testid={`plan-card-${plan.id}-banner`}
        >
          {banner}
        </span>
        {isRecommended && (
          <span
            className="ml-2 inline-flex items-center gap-1 rounded-full bg-velcor-green text-white px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.15em] align-middle"
            data-testid={`plan-card-${plan.id}-recommended`}
          >
            <Sparkles className="w-2.5 h-2.5" />
            Recomendado
          </span>
        )}
      </div>

      {/* Body — corporate breathing room */}
      <div className="px-6 pt-6 pb-6 flex-1 flex flex-col">
        {/* Plan name + term */}
        <h3
          className="text-velcor-ink leading-[1.1] mb-1"
          style={{ fontSize: "clamp(18px, 1.8vw, 21px)", fontWeight: 800, letterSpacing: "-0.018em" }}
        >
          {plan.name}
        </h3>
        <div className="text-[11px] font-bold text-slate-500 mb-4 tracking-tight uppercase">
          {plan.term}
        </div>

        {/* Description — generous height for corporate feel */}
        <p className="text-slate-500 text-[12.5px] leading-relaxed mb-6 min-h-[52px]">
          {plan.description}
        </p>

        {/* Horizontal divider */}
        <div className="border-t border-slate-200 mb-6" />

        {/* Big $/mes headline — Constellation-style ($XXX big, rate small) */}
        <div className="text-center mb-7" data-testid={`plan-price-${plan.id}`}>
          <div className="inline-flex items-start justify-center gap-0.5">
            <span
              className="text-velcor-ink leading-none mt-2"
              style={{ fontSize: "clamp(20px, 2.2vw, 24px)", fontWeight: 900, letterSpacing: "-0.022em" }}
            >
              $
            </span>
            <span
              className="text-velcor-ink tabular-nums leading-none"
              style={{ fontSize: "clamp(42px, 4.8vw, 54px)", fontWeight: 900, letterSpacing: "-0.04em" }}
              data-testid={`plan-price-amount-${plan.id}`}
            >
              {pricing.monthly}
            </span>
            <button
              type="button"
              onClick={onToggleDetails}
              className="mt-2 ml-1 w-4 h-4 grid place-items-center rounded-full border border-velcor-green/30 text-velcor-green hover:bg-velcor-green hover:text-white transition-colors shrink-0"
              aria-label="Más información"
              data-testid={`plan-info-${plan.id}`}
            >
              <span className="font-extrabold text-[9px]">i</span>
            </button>
          </div>
          <div className="text-[11px] text-slate-500 font-bold mt-1.5 tabular-nums">
            factura mensual estimada
          </div>
          <div
            className="mt-3 inline-flex items-center gap-1 rounded-full bg-slate-50 px-3 py-1 text-[11px] font-bold text-slate-600 tabular-nums"
            data-testid={`plan-monthly-${plan.id}`}
          >
            <span
              className="text-velcor-ink font-extrabold"
              data-testid={`plan-price-rate-${plan.id}`}
            >
              {pricing.planRate}¢
            </span>
            por kWh a {pricing.usage.toLocaleString("en-US")} kWh
          </div>
        </div>

        {/* CTAs — generous corporate spacing */}
        <div className="mt-auto space-y-2.5">
          <button
            onClick={onChoose}
            data-testid={`plan-choose-${plan.id}`}
            className="w-full h-11 rounded-full bg-velcor-green text-white font-extrabold text-[13px] tracking-tight inline-flex items-center justify-center gap-2 hover:bg-[#256330] transition-all hover:shadow-[0_8px_20px_rgba(47,122,58,0.25)]"
          >
            Elegir este plan
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={onToggleDetails}
            className="w-full h-11 rounded-full border-2 border-velcor-green text-velcor-green bg-white hover:bg-velcor-green/5 font-extrabold text-[12.5px] tracking-tight inline-flex items-center justify-center gap-2 transition-colors"
            data-testid={`plan-details-toggle-${plan.id}`}
          >
            Más información
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Velcor support number */}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-[10.5px] font-bold text-slate-400">
          <Phone className="w-3 h-3" strokeWidth={2.4} />
          <a href="tel:+18007156516" className="hover:text-velcor-green transition-colors tabular-nums">
            800-715-6516
          </a>
        </div>
      </div>

      {/* Detail modal (portal-style overlay) — replaces inline disclosure */}
      <PlanDetailsModal
        plan={plan}
        pricing={pricing}
        open={detailsOpen}
        onClose={onToggleDetails}
        onChoose={onChoose}
      />
    </motion.div>
  );
}

/* ============================================================
   PlanDetailsModal — full-screen overlay with plan benefits.
   Replaces the old inline AnimatePresence disclosure for a more
   focused, premium UX (Constellation-style).
   ============================================================ */
function PlanDetailsModal({ plan, pricing, open, onClose, onChoose }) {
  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          data-testid={`plan-modal-${plan.id}`}
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-velcor-ink/60 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />

          {/* Modal panel */}
          <motion.div
            className="relative w-full max-w-[520px] max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-[0_30px_80px_-30px_rgba(15,29,61,0.5)]"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`plan-modal-title-${plan.id}`}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white z-10 border-b border-slate-100 px-6 py-4 flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green mb-1">
                  {plan.eyebrow}
                </div>
                <h2
                  id={`plan-modal-title-${plan.id}`}
                  className="text-velcor-ink leading-[1.1]"
                  style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.018em" }}
                >
                  {plan.name}
                </h2>
                <div className="text-[12px] font-bold text-slate-500 mt-0.5 uppercase tracking-tight">
                  {plan.term}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                data-testid={`plan-modal-close-${plan.id}`}
                className="ml-3 w-9 h-9 grid place-items-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors shrink-0"
              >
                <X className="w-4 h-4" strokeWidth={2.6} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5">
              {/* Price summary */}
              <div className="rounded-2xl bg-velcor-green/8 border border-velcor-green/20 px-4 py-3 flex items-baseline gap-3">
                <div>
                  <div className="text-velcor-ink leading-none" style={{ fontSize: "32px", fontWeight: 900, letterSpacing: "-0.03em" }}>
                    ${pricing.monthly}
                    <span className="text-[13px] font-bold text-slate-500 ml-1">/mes</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-bold mt-1 tabular-nums">
                    {pricing.planRate}¢ por kWh a {pricing.usage.toLocaleString("en-US")} kWh
                  </div>
                </div>
              </div>

              <p className="text-slate-600 text-[13px] leading-relaxed">
                {plan.description}
              </p>

              <div>
                <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-slate-400 mb-3">
                  Lo que incluye este plan
                </div>
                <ul className="space-y-2.5">
                  {plan.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[13px] text-slate-700">
                      <span className="grid place-items-center w-4 h-4 rounded-full bg-velcor-green/15 text-velcor-green shrink-0 mt-0.5">
                        <Check className="w-2.5 h-2.5" strokeWidth={3.2} />
                      </span>
                      <span className="leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-wrap gap-3 text-[12px] pt-1 border-t border-slate-100 pt-4">
                <a
                  href={`/electricity-facts-label/${plan.id}`}
                  className="text-velcor-green font-bold hover:underline inline-flex items-center gap-1"
                >
                  EFL <ArrowRight className="w-3 h-3" />
                </a>
                <a
                  href="/terms"
                  className="text-velcor-green font-bold hover:underline inline-flex items-center gap-1"
                >
                  Términos <ArrowRight className="w-3 h-3" />
                </a>
                <a
                  href="/yrac"
                  className="text-velcor-green font-bold hover:underline inline-flex items-center gap-1"
                >
                  Derechos del cliente <ArrowRight className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Sticky footer CTA */}
            <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-3">
              <button
                onClick={() => { onClose?.(); onChoose?.(); }}
                data-testid={`plan-modal-choose-${plan.id}`}
                className="w-full h-11 rounded-full bg-velcor-green text-white font-extrabold text-[13.5px] tracking-tight inline-flex items-center justify-center gap-2 hover:bg-[#256330] transition-all hover:shadow-[0_8px_20px_rgba(47,122,58,0.25)]"
              >
                Elegir este plan
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <span className="grid place-items-center w-9 h-9 rounded-lg bg-white border border-slate-200 text-velcor-green">
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </span>
      <span className="text-[11px] font-extrabold text-slate-600 leading-tight">{label}</span>
    </div>
  );
}


